import { registerBlockType } from "@wordpress/blocks";
import {
    InspectorControls,
    MediaUpload,
    MediaUploadCheck,
    useBlockProps,
} from "@wordpress/block-editor";
import {
    PanelBody,
    Button,
    TextControl,
    SelectControl,
    ToggleControl,
    __experimentalView as View,
} from "@wordpress/components";
import ResponsiveRange from "./components/ResponsiveRange";
import CustomSwitch from "./components/CustomSwitch";

registerBlockType("danone/responsive-image", {
    apiVersion: 2,
    title: "Responsive Image",
    icon: "images-alt2",
    category: "media",

    attributes: {
        imageUrl: {
            type: "string",
            default: "",
        },
        imageId: {
            type: "number",
        },
        altText: {
            type: "string",
            default: "",
        },
        imageTitle: {
            type: "string",
            default: "",
        },
        widthDesktop: {
            type: "object",
            default: {
                value: 100,
                valueUnit: "%",
            },
        },
        widthTablet: {
            type: "object",
            default: {
                value: 100,
                valueUnit: "%",
            },
        },
        widthMobile: {
            type: "object",
            default: {
                value: 100,
                valueUnit: "%",
            },
        },
        heightDesktop: {
            type: "object",
            default: {
                value: "auto",
                valueUnit: "px",
            },
        },
        heightTablet: {
            type: "object",
            default: {
                value: "auto",
                valueUnit: "px",
            },
        },
        heightMobile: {
            type: "object",
            default: {
                value: "auto",
                valueUnit: "px",
            },
        },
        borderRadiusDesktop: {
            type: "object",
            default: {
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                topUnit: "px",
                rightUnit: "px",
                bottomUnit: "px",
                leftUnit: "px",
            },
        },
        borderRadiusTablet: {
            type: "object",
            default: {
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                topUnit: "px",
                rightUnit: "px",
                bottomUnit: "px",
                leftUnit: "px",
            },
        },
        borderRadiusMobile: {
            type: "object",
            default: {
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                topUnit: "px",
                rightUnit: "px",
                bottomUnit: "px",
                leftUnit: "px",
            },
        },
        objectFit: {
            type: "string",
            default: "cover",
        },
        isLazyLoad: {
            type: "boolean",
            default: true,
        },
        linkUrl: {
            type: "string",
            default: "",
        },
        openInNewTab: {
            type: "boolean",
            default: false,
        },
    },

    edit: function ResponsiveImageEdit({ attributes, setAttributes }) {
        const blockProps = useBlockProps();

        const updateValue = (
            name,
            device,
            direction,
            value,
            unit,
            isComplete
        ) => {
            const attributeName = `${name}${
                device.charAt(0).toUpperCase() + device.slice(1)
            }`;

            if (name === "width" || name === "height") {
                setAttributes({
                    [attributeName]: {
                        value: value,
                        valueUnit: unit,
                    },
                });
            } else {
                // Dla border-radius
                const currentValue = attributes[attributeName] || {};
                setAttributes({
                    [attributeName]: {
                        ...currentValue,
                        [direction]: value,
                        [`${direction}Unit`]: unit,
                    },
                });
            }
        };

        const onReset = (name, device) => {
            const attributeName = `${name}${
                device.charAt(0).toUpperCase() + device.slice(1)
            }`;

            if (name === "width") {
                setAttributes({
                    [attributeName]: {
                        value: 100,
                        valueUnit: "%",
                    },
                });
            } else if (name === "height") {
                setAttributes({
                    [attributeName]: {
                        value: "auto",
                        valueUnit: "px",
                    },
                });
            } else {
                // Dla border-radius
                setAttributes({
                    [attributeName]: {
                        top: 0,
                        right: 0,
                        bottom: 0,
                        left: 0,
                        topUnit: "px",
                        rightUnit: "px",
                        bottomUnit: "px",
                        leftUnit: "px",
                    },
                });
            }
        };

        return (
            <>
                <InspectorControls>
                    <PanelBody title="Ustawienia obrazu" initialOpen={true}>
                        <MediaUploadCheck>
                            <MediaUpload
                                onSelect={(media) => {
                                    setAttributes({
                                        imageUrl: media.url,
                                        imageId: media.id,
                                        altText: media.alt || "",
                                        imageTitle: media.title || "",
                                    });
                                }}
                                allowedTypes={["image"]}
                                value={attributes.imageId}
                                render={({ open }) => (
                                    <Button
                                        onClick={open}
                                        variant="secondary"
                                        style={{
                                            marginBottom: "10px",
                                            width: "100%",
                                        }}
                                    >
                                        {attributes.imageUrl
                                            ? "Zmień obraz"
                                            : "Wybierz obraz"}
                                    </Button>
                                )}
                            />
                        </MediaUploadCheck>

                        {attributes.imageUrl && (
                            <Button
                                onClick={() => {
                                    setAttributes({
                                        imageUrl: "",
                                        imageId: undefined,
                                        altText: "",
                                        imageTitle: "",
                                    });
                                }}
                                variant="link"
                                isDestructive
                                style={{ marginBottom: "15px" }}
                            >
                                Usuń obraz
                            </Button>
                        )}

                        <TextControl
                            label="URL obrazu"
                            value={attributes.imageUrl}
                            onChange={(value) =>
                                setAttributes({ imageUrl: value })
                            }
                            style={{ marginBottom: "15px" }}
                        />

                        <TextControl
                            label="Tekst alternatywny"
                            value={attributes.altText}
                            onChange={(value) =>
                                setAttributes({ altText: value })
                            }
                            style={{ marginBottom: "15px" }}
                        />

                        <SelectControl
                            label="Dopasowanie obrazu"
                            value={attributes.objectFit}
                            options={[
                                { label: "Cover", value: "cover" },
                                { label: "Contain", value: "contain" },
                                { label: "Fill", value: "fill" },
                                { label: "None", value: "none" },
                            ]}
                            onChange={(value) =>
                                setAttributes({ objectFit: value })
                            }
                        />

                        <TextControl
                            label="Link URL"
                            value={attributes.linkUrl}
                            onChange={(value) =>
                                setAttributes({ linkUrl: value })
                            }
                            style={{ marginBottom: "15px" }}
                        />

                        {attributes.linkUrl && (
                            <CustomSwitch
                                label="Otwórz w nowym oknie"
                                checked={attributes.openInNewTab}
                                onChange={({ target: { checked } }) =>
                                    setAttributes({ openInNewTab: checked })
                                }
                                size="default"
                            />
                        )}

                        <CustomSwitch
                            label="Lazy loading"
                            checked={attributes.isLazyLoad}
                            onChange={({ target: { checked } }) =>
                                setAttributes({ isLazyLoad: checked })
                            }
                            size="default"
                        />
                    </PanelBody>

                    <PanelBody title="Wymiary" initialOpen={false}>
                        <View style={{ marginBottom: "20px" }}>
                            <ResponsiveRange
                                type="single"
                                name="width"
                                attributes={{
                                    widthDesktop: attributes.widthDesktop,
                                    widthTablet: attributes.widthTablet,
                                    widthMobile: attributes.widthMobile,
                                }}
                                updateValue={updateValue}
                                onReset={onReset}
                            />
                        </View>

                        <View style={{ marginBottom: "20px" }}>
                            <ResponsiveRange
                                type="single"
                                name="height"
                                attributes={{
                                    heightDesktop: attributes.heightDesktop,
                                    heightTablet: attributes.heightTablet,
                                    heightMobile: attributes.heightMobile,
                                }}
                                updateValue={updateValue}
                                onReset={onReset}
                            />
                        </View>
                    </PanelBody>

                    <PanelBody title="Zaokrąglenie" initialOpen={false}>
                        <ResponsiveRange
                            type="multi"
                            name="borderRadius"
                            attributes={{
                                borderRadiusDesktop:
                                    attributes.borderRadiusDesktop,
                                borderRadiusTablet:
                                    attributes.borderRadiusTablet,
                                borderRadiusMobile:
                                    attributes.borderRadiusMobile,
                            }}
                            updateValue={updateValue}
                            onReset={onReset}
                        />
                    </PanelBody>
                </InspectorControls>

                <div {...blockProps}>
                    {attributes.imageUrl ? (
                        attributes.linkUrl ? (
                            <a
                                href={attributes.linkUrl}
                                target={
                                    attributes.openInNewTab ? "_blank" : "_self"
                                }
                                rel={
                                    attributes.openInNewTab
                                        ? "noopener noreferrer"
                                        : ""
                                }
                            >
                                <img
                                    src={attributes.imageUrl}
                                    alt={attributes.altText}
                                    title={attributes.imageTitle}
                                    style={{
                                        width: `${attributes.widthDesktop.value}${attributes.widthDesktop.valueUnit}`,
                                        height:
                                            attributes.heightDesktop.value ===
                                            "auto"
                                                ? "auto"
                                                : `${attributes.heightDesktop.value}${attributes.heightDesktop.valueUnit}`,
                                        objectFit: attributes.objectFit,
                                        borderRadius: `${attributes.borderRadiusDesktop.top}${attributes.borderRadiusDesktop.topUnit} ${attributes.borderRadiusDesktop.right}${attributes.borderRadiusDesktop.rightUnit} ${attributes.borderRadiusDesktop.bottom}${attributes.borderRadiusDesktop.bottomUnit} ${attributes.borderRadiusDesktop.left}${attributes.borderRadiusDesktop.leftUnit}`,
                                    }}
                                />
                            </a>
                        ) : (
                            <img
                                src={attributes.imageUrl}
                                alt={attributes.altText}
                                title={attributes.imageTitle}
                                style={{
                                    width: `${attributes.widthDesktop.value}${attributes.widthDesktop.valueUnit}`,
                                    height:
                                        attributes.heightDesktop.value ===
                                        "auto"
                                            ? "auto"
                                            : `${attributes.heightDesktop.value}${attributes.heightDesktop.valueUnit}`,
                                    objectFit: attributes.objectFit,
                                    borderRadius: `${attributes.borderRadiusDesktop.top}${attributes.borderRadiusDesktop.topUnit} ${attributes.borderRadiusDesktop.right}${attributes.borderRadiusDesktop.rightUnit} ${attributes.borderRadiusDesktop.bottom}${attributes.borderRadiusDesktop.bottomUnit} ${attributes.borderRadiusDesktop.left}${attributes.borderRadiusDesktop.leftUnit}`,
                                }}
                            />
                        )
                    ) : (
                        <div
                            style={{
                                backgroundColor: "#f0f0f0",
                                padding: "20px",
                                textAlign: "center",
                            }}
                        >
                            Wybierz obraz
                        </div>
                    )}
                </div>
            </>
        );
    },

    save: function ResponsiveImageSave({ attributes }) {
        const blockProps = useBlockProps.save();

        // Przygotowanie zmiennych CSS dla różnych urządzeń
        const responsiveStyles = {
            // Width
            "--width-desktop": `${attributes.widthDesktop.value}${attributes.widthDesktop.valueUnit}`,
            "--width-tablet": `${attributes.widthTablet.value}${attributes.widthTablet.valueUnit}`,
            "--width-mobile": `${attributes.widthMobile.value}${attributes.widthMobile.valueUnit}`,

            // Height
            "--height-desktop":
                attributes.heightDesktop.value === "auto"
                    ? "auto"
                    : `${attributes.heightDesktop.value}${attributes.heightDesktop.valueUnit}`,
            "--height-tablet":
                attributes.heightTablet.value === "auto"
                    ? "auto"
                    : `${attributes.heightTablet.value}${attributes.heightTablet.valueUnit}`,
            "--height-mobile":
                attributes.heightMobile.value === "auto"
                    ? "auto"
                    : `${attributes.heightMobile.value}${attributes.heightMobile.valueUnit}`,

            // Border Radius
            "--border-radius-desktop": `${attributes.borderRadiusDesktop.top}${attributes.borderRadiusDesktop.topUnit} ${attributes.borderRadiusDesktop.right}${attributes.borderRadiusDesktop.rightUnit} ${attributes.borderRadiusDesktop.bottom}${attributes.borderRadiusDesktop.bottomUnit} ${attributes.borderRadiusDesktop.left}${attributes.borderRadiusDesktop.leftUnit}`,
            "--border-radius-tablet": `${attributes.borderRadiusTablet.top}${attributes.borderRadiusTablet.topUnit} ${attributes.borderRadiusTablet.right}${attributes.borderRadiusTablet.rightUnit} ${attributes.borderRadiusTablet.bottom}${attributes.borderRadiusTablet.bottomUnit} ${attributes.borderRadiusTablet.left}${attributes.borderRadiusTablet.leftUnit}`,
            "--border-radius-mobile": `${attributes.borderRadiusMobile.top}${attributes.borderRadiusMobile.topUnit} ${attributes.borderRadiusMobile.right}${attributes.borderRadiusMobile.rightUnit} ${attributes.borderRadiusMobile.bottom}${attributes.borderRadiusMobile.bottomUnit} ${attributes.borderRadiusMobile.left}${attributes.borderRadiusMobile.leftUnit}`,
        };

        const imageElement = (
            <img
                src={attributes.imageUrl}
                alt={attributes.altText}
                title={attributes.imageTitle}
                loading={attributes.isLazyLoad ? "lazy" : "eager"}
                style={{
                    width: "var(--width-desktop)",
                    height: "var(--height-desktop)",
                    objectFit: attributes.objectFit,
                    borderRadius: "var(--border-radius-desktop)",
                }}
                className="responsive-image"
            />
        );

        return (
            <div {...blockProps} style={responsiveStyles}>
                {attributes.imageUrl &&
                    (attributes.linkUrl ? (
                        <a
                            href={attributes.linkUrl}
                            target={
                                attributes.openInNewTab ? "_blank" : "_self"
                            }
                            rel={
                                attributes.openInNewTab
                                    ? "noopener noreferrer"
                                    : ""
                            }
                        >
                            {imageElement}
                        </a>
                    ) : (
                        imageElement
                    ))}
            </div>
        );
    },
});
